import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { cloudFunctionV2 } from '../functions/api';

const PAGE_SIZE = 25;

// Explicit field projection for the leads-list view. Each entry is a
// dotted path under `data.` — they get expanded into Firestore .select()
// server-side, slimming the wire payload from ~2 MB/page to ~150 KB/page.
//
// Notably EXCLUDED (saves ~80% per doc): buyerMatches, buyerMatchIds,
// enrichment, rawData. These are detail-page-only fields the table never
// reads.
//
// repairCostImageUrls + photoOrder ARE included so map-view LeadCard
// thumbnails match what LeadDetailV2 shows — repair-analysis photos are
// the highest-quality source we have and they were missing from card
// previews. Adds ~1-3 KB/lead to the wire payload, accepted trade.
//
// If a column ever needs a field outside this set, add the dotted path
// here. Forgetting is failsafe — the cell just renders empty until you
// add it. (Server still falls back to returning the full doc if `fields`
// isn't passed.)
// ⚠️ Failsafe, but SILENT, and that is not the same as harmless: an empty cell is
// indistinguishable from a lead that genuinely has nothing there, so a column can
// render blank for everyone indefinitely and look correct. notes /
// acquisitionComments / acquisitionNotes did exactly that. When adding a column,
// check this list first — a blank column is the expected symptom, not an error.
const LEADS_LIST_FIELDS = [
    'leadId', 'propertyId', 'leadType', 'isArchived',
    // Manual submissions kept despite being outside the buy box carry this flag
    // so the table can show an "Out of buy box" chip next to the address.
    // `outsideBuyBoxReasons` rides along because the chip is NOT always "Out of
    // buy box": when the sole reason is `geography_unknown` we never located the
    // property, so claiming it is outside the area asserts something we never
    // established, and the chip reads "Location unknown" instead. Without this
    // field that check silently reads undefined and always picks the wrong
    // branch — failsafe as the comment above says, but wrong.
    'outsideBuyBox', 'outsideBuyBoxReasons',
    'createdAt', 'updatedAt', 'completedAt',
    'address', 'latitude', 'longitude', 'countyId',
    'propertyType', 'propertyTypeVerification',
    'verifiedCharacteristics',
    'currentAskingPrice', 'originalAskingPrice', 'ingestedAskingPrice',
    'estimates', 'computedSpread', 'computedMaoVsAsking',
    'leadReviewStatus', 'dealStatus',
    'triage',
    // The Notes and Acquisition Notes columns. Omitting these did not hide the columns —
    // it made them render their EMPTY state on every row, which reads as "this lead has
    // no notes" rather than as a missing field. An inline edit looked like it worked
    // (Leads.js writes the new value onto the local row) and then vanished on refresh.
    // Measured at ~103 B/lead average on a 300-lead sample, ~2.5 KB per page of 25 —
    // and 159 of those 300 leads had one of the three set.
    'notes', 'acquisitionComments', 'acquisitionNotes',
    // Stage-2 strategy-fit verdicts (data.strategyFit — array of { strategy, fits, reason,
    // value, threshold, display }) drive the deal-value badges in LeadsTable, and
    // buyerMatchCount feeds the "matched N buyers" chip.
    // 🚨 Project the COUNT, never data.buyerMatchScores. That map used to be bounded at
    // MAX_STORED_MATCHES (40) and is now derived from the matcher's uncapped list, so it
    // grows with the roster — 40 entries is 2.6 KB, 195 is 12.7 KB, and at PAGE_SIZE 25
    // that turned a ~64 KB page into ~310 KB (a 1,000-buyer match would be ~1.5 MB) purely
    // to call Object.keys(...).length. The heavy buyerMatches blob stays excluded too.
    'strategyFit', 'buyerMatchCount',
    // Co-living deal ranking (functions/utils/coLivingCashOutRank.js), mirrored onto the lead
    // doc by syncLeadUnderwriting. `coLivingFinancingPath` is 'bridgeRefi' for houses rough enough
    // that a DSCR lender won't write them as-is and 'dscr' for the rest; it drives the Financing
    // filter AND the client-side fallback for which paths that filter offers (see Leads.js's
    // `availableFinancingPaths`), so dropping it from the projection blanks both.
    'coLivingFinancingPath',
    // The deal SCORE, 0-100: the discount the deal needs (MAO ÷ ask), adjusted by whether it
    // cash flows at that loan. Sort DESC for best-first.
    'coLivingDealScore', 'coLivingMao', 'coLivingCashFlowGrade', 'coLivingNcfAtMao',
    // The co-living RESALE MAO — OUR price when present (functions/utils/ourPrice.js), and what the
    // "Our Price vs Ask" column names beside its gap. 🚨 Omit it and every row silently falls back to
    // the hold or flip MAO and labels that as our price, while the gap was computed from the resale one.
    'coLivingCtvMao',
    // The turnkey MAO, which comes before it, and the fee our price takes off (decision 0106).
    // Same trap: omit either and the column's price disagrees with the gap beside it.
    'coLivingTurnkeyMao',
    // The sliding price (decision 0107), which our price reads before the turnkey one. Same trap: omit it and the column's
    // price disagrees with the gap beside it.
    'coLivingSlidingMao',
    // A named buyer's price (decision 0109), which our price reads before every other. Same trap.
    'coLivingBuyerMao',
    // ...and the fee struck with it, which our price takes off it (an older rank stored none on the lead).
    'coLivingBuyerMaoFor',
    'coLivingWholesaleFee',
    // MAO as a percent of ask, uncapped — the "MAO vs Ask (co-living)" column and its sort key.
    // The unadjusted half of the score above: how small a discount the deal needs, with no
    // cash-flow judgement laid over it. Sort DESC — closest to ask first.
    'coLivingMaoPctOfAsk',
    // NOTE: data.dealDisposition exists on leads (matcher.js → buildBuyerMatchSummary)
    // but is deliberately NOT projected — the table has no consumer (the disposition
    // chip was dropped as redundant with the strategy-fit + buyer-demand chips).
    // Re-add here if a Disposition filter or the self-purchase flow ever needs it
    // in the list view.
    'assigned', 'assignedTo', 'assignedUserIds',
    'imageUrls', 'imageFolders', 'imageDownloadStatus', 'photoUrls', 'photoAvailability',
    'repairCostImageUrls', 'photoOrder', 'featuredImageUrl',
    // Operator-deleted photos. Needed because the "No photos" badge now resolves
    // through deriveDisplayImageUrls, which subtracts these — without it the table
    // would count a photo the operator removed and stay silent on a lead that really
    // has nothing left. Small array; the badge is the only consumer here.
    'excludedImageUrls',
    'distressSignals',
    'motivationScore', 'motivationStrike', 'motivationTier', 'motivationTopSignals',
    'entries', 'currentSender', 'originalSender', 'senderMissing',
    // Classifier-detected sender company logo (a logo image found in the deal's
    // photos). Resolved with the domain-favicon fallback by resolveSenderLogo to
    // brand the Main contact cell.
    'senderLogoImageUrl',
    'customFields',
    // Foreclosure/trustee-sale fields (null for non-auction leads). Required for
    // the auction filters (status / lienPosition / dateEpoch) to query the
    // projected doc, and to surface auction status as a table signal.
    'auction',
    'automationId', 'portfolioId', 'portfolioName',
    // Source-channel resolution: `source` / `originSource` are the lead's
    // ORIGIN and fully determine the source icon (resolveLeadSourceProfile) —
    // including the GoHighLevel / FUB / Salesforce icons, which show only when
    // the lead originated from that CRM's webhook. `sourceMetadata` carries
    // inboxProvider (gmail/outlook) + toEmail (specific inbox the lead arrived
    // at) for the tooltip and the detail-page "Received at" sub-line.
    // `crmContactUrl` is a CRM linkage artifact (reverse-lookup match or
    // outbound sync) — kept only for the "view in CRM" link, NOT the icon.
    'source', 'originSource', 'sourceMetadata',
    // smsWriteBack.authSource ('user-forward' vs 'company-twilio') drives the
    // Messages-vs-Twilio source icon for SMS leads (resolveLeadSourceProfile).
    'smsWriteBack',
    'crmContactUrl',
    'sourceUrls',
    'underwriting.status', 'underwriting.maxAllowableOffer',
    'underwriting.arv', 'underwriting.repairCost', 'underwriting.repairCostEstimated',
    'underwriting.arvSkipReason', 'underwriting.repairSkipReason',
    'underwriting.estimatedProfit', 'underwriting.estimatedProfitPercentage',
    'underwriting.reason', 'underwriting.details',
    // Storyboard step for the async underwriting calc. Without this projected
    // the leads list strips it, and resolveArvStatus/resolveRepairsStatus fall
    // through to the generic "ARV calculation did not complete — reason not
    // recorded on the lead" fallback while underwriting is still running
    // (top-level data.status is written 'complete' at lead creation, so the
    // step is the only signal that says "in flight"). See underwritingStatus.js.
    'processingSteps.underwriting',
    'dealUrls',
    'sourceFavicons',
    'dedupHits',
    'status',
    'processingStage',
    'flags',
    'syntheticPropertyId'
];

const FIELD_MAP = {
    createdAt: 'data.createdAt',
    address: 'data.address.streetAddress',
    beds: 'data.verifiedCharacteristics.bedrooms',
    baths: 'data.verifiedCharacteristics.bathrooms',
    sqft: 'data.verifiedCharacteristics.livingSquareFeet',
    yearBuilt: 'data.verifiedCharacteristics.yearBuilt',
    askingPrice: 'data.ingestedAskingPrice',
    zillowEstimate: 'data.estimates.zillow',
    redfinEstimate: 'data.estimates.redfin',
    realtorEstimate: 'data.estimates.realtor',
    spread: 'data.computedSpread',
    maoVsAsking: 'data.computedMaoVsAsking',
    propertyType: 'data.propertyType',
    leadType: 'data.leadType',
    updatedAt: 'data.updatedAt',
    leadReviewStatus: 'data.leadReviewStatus',
    dealStatus: 'data.dealStatus',
    dealScore: 'data.coLivingDealScore',
    maoVsAsk: 'data.coLivingMaoPctOfAsk',
    priorityScore: 'data.triage.priority_score',
    priorityTier: 'data.triage.priority_tier',
    triageBucket: 'data.triage.bucket',
    triageScoredAt: 'data.triage.scored_at'
};

export const getFirestoreFieldPath = (column) => FIELD_MAP[column] || 'data.createdAt';

// The column keys that resolve to a real Firestore path. `getFirestoreFieldPath` falls back to
// createdAt for anything else, which is right for a stale bookmarked URL but wrong for a stored
// company default — that would silently sort by date while claiming to honour the setting.
export const isSortableColumn = (column) => Object.prototype.hasOwnProperty.call(FIELD_MAP, column);

// Build the params payload that both leadsBootstrap and getOrderedDocs accept.
// Pure function — easy to memo a stable JSON key from its output.
export function buildLeadsParams({
    sortColumn,
    sortDirection,
    page,
    leadTypeFilter,
    debouncedSearch,
    selectedSources,
    automationIdFilter,
    bounds,
    archivedFilter,
    propertyFilters = {}
}) {
    const firestoreField = getFirestoreFieldPath(sortColumn || 'createdAt');
    const firestoreOrder = sortDirection || 'desc';
    const applyLeadType = archivedFilter ? null : leadTypeFilter;
    const applySourceFilter = applyLeadType && selectedSources && selectedSources.length > 0;
    const applyAutomationFilter = !applySourceFilter && automationIdFilter;

    const rangeFilters = [];
    const stringFilters = [];

    if (propertyFilters.priceMin || propertyFilters.priceMax) {
        rangeFilters.push({
            field: 'data.currentAskingPrice',
            min: propertyFilters.priceMin ? Number(String(propertyFilters.priceMin).replace(/[^0-9]/g, '')) : null,
            max: propertyFilters.priceMax ? Number(String(propertyFilters.priceMax).replace(/[^0-9]/g, '')) : null
        });
    }
    if (propertyFilters.bedsMin || propertyFilters.bedsMax) {
        rangeFilters.push({
            field: 'data.verifiedCharacteristics.bedrooms',
            min: propertyFilters.bedsMin ? Number(propertyFilters.bedsMin) : null,
            max: propertyFilters.bedsMax ? Number(propertyFilters.bedsMax) : null
        });
    }
    if (propertyFilters.bathsMin || propertyFilters.bathsMax) {
        rangeFilters.push({
            field: 'data.verifiedCharacteristics.bathrooms',
            min: propertyFilters.bathsMin ? Number(propertyFilters.bathsMin) : null,
            max: propertyFilters.bathsMax ? Number(propertyFilters.bathsMax) : null
        });
    }
    if (propertyFilters.sqftMin || propertyFilters.sqftMax) {
        rangeFilters.push({
            field: 'data.verifiedCharacteristics.livingSquareFeet',
            min: propertyFilters.sqftMin ? Number(propertyFilters.sqftMin) : null,
            max: propertyFilters.sqftMax ? Number(propertyFilters.sqftMax) : null
        });
    }
    if (propertyFilters.yearBuiltMin || propertyFilters.yearBuiltMax) {
        rangeFilters.push({
            field: 'data.verifiedCharacteristics.yearBuilt',
            min: propertyFilters.yearBuiltMin ? Number(propertyFilters.yearBuiltMin) : null,
            max: propertyFilters.yearBuiltMax ? Number(propertyFilters.yearBuiltMax) : null
        });
    }
    if (propertyFilters.spreadMin) {
        rangeFilters.push({ field: 'data.computedSpread', min: Number(propertyFilters.spreadMin), max: null });
    }
    if (propertyFilters.maoVsAskingMax) {
        rangeFilters.push({
            field: 'data.computedMaoVsAsking',
            min: -Math.abs(Number(propertyFilters.maoVsAskingMax)),
            max: null
        });
    }
    // Co-living financing path — HOW the operator would buy the house. 'bridgeRefi' is a house
    // in bad enough shape that a DSCR lender won't finance it as-is (cash/hard money → rehab →
    // refinance); 'dscr' is one clean enough to finance from day one. See
    // functions/utils/coLivingPathSelection.js — those two strings are the persisted values.
    //
    // Selecting BOTH is not the same as selecting neither: it still excludes every lead with no
    // path at all (non-co-living, or co-living that never priced).
    //
    // `bridgeRefiOnly: true` is the legacy single-boolean shape, still reachable from a bookmarked
    // URL or a stale `filters` param, and is read as ['bridgeRefi'].
    //
    // getOrderedDocs resolves the dotted path in memory (getNestedValue), and the leads
    // subcollection always takes that path, so this needs no Firestore index.
    const financingPaths = Array.isArray(propertyFilters.financingPaths) && propertyFilters.financingPaths.length > 0
        ? propertyFilters.financingPaths
        : (propertyFilters.bridgeRefiOnly ? ['bridgeRefi'] : []);
    if (financingPaths.length > 0) {
        stringFilters.push({ field: 'data.coLivingFinancingPath', value: financingPaths, matchType: 'in' });
    }
    // "Only show deals scoring at least N." A range MIN, because a HIGHER score is better.
    if (propertyFilters.dealScoreMin) {
        rangeFilters.push({
            field: 'data.coLivingDealScore',
            min: Number(propertyFilters.dealScoreMin),
            max: null
        });
    }
    if (propertyFilters.dateMin || propertyFilters.dateMax) {
        rangeFilters.push({
            field: 'data.createdAt._seconds',
            min: propertyFilters.dateMin ? Math.floor(new Date(propertyFilters.dateMin).getTime() / 1000) : null,
            max: propertyFilters.dateMax ? Math.floor(new Date(propertyFilters.dateMax + 'T23:59:59').getTime() / 1000) : null
        });
    }
    if (propertyFilters.state) {
        stringFilters.push({ field: 'data.address.state', value: propertyFilters.state, matchType: 'exact' });
    }
    if (propertyFilters.city) {
        stringFilters.push({ field: 'data.address.city', value: propertyFilters.city, matchType: 'prefix' });
    }
    if (propertyFilters.propertyType && propertyFilters.propertyType.length > 0) {
        stringFilters.push({ field: 'data.propertyType', value: propertyFilters.propertyType, matchType: 'in' });
    }
    if (propertyFilters.dealStatuses && propertyFilters.dealStatuses.length > 0) {
        stringFilters.push({ field: 'data.dealStatus', value: propertyFilters.dealStatuses, matchType: 'in' });
    }
    if (propertyFilters.senders && propertyFilters.senders.length > 0) {
        stringFilters.push({ field: 'data.originalSender.name', value: propertyFilters.senders, matchType: 'in' });
    }
    if (propertyFilters.portfolios && propertyFilters.portfolios.length > 0) {
        stringFilters.push({ field: 'data.portfolioId', value: propertyFilters.portfolios, matchType: 'in' });
    }
    // Auction (foreclosure / trustee-sale) filters — Tier 1. Nested paths are
    // queried in-memory by getOrderedDocs (getNestedValue), so no Firestore
    // index is needed. lienPosition is stored as a number; the backend 'in'
    // match stringifies both sides, so [1, 2] matches stored 1 / 2.
    if (propertyFilters.auctionStatus && propertyFilters.auctionStatus.length > 0) {
        stringFilters.push({ field: 'data.auction.status', value: propertyFilters.auctionStatus, matchType: 'in' });
    }
    if (propertyFilters.lienPosition && propertyFilters.lienPosition.length > 0) {
        stringFilters.push({ field: 'data.auction.lienPosition', value: propertyFilters.lienPosition, matchType: 'in' });
    }
    if (propertyFilters.auctionDateMin || propertyFilters.auctionDateMax) {
        // auction.dateEpoch is stored at UTC midnight of the auction day, so
        // parse the date-input bounds as UTC too (a UTC end-of-day max so the
        // chosen last day is inclusive).
        rangeFilters.push({
            field: 'data.auction.dateEpoch',
            min: propertyFilters.auctionDateMin ? Math.floor(new Date(propertyFilters.auctionDateMin + 'T00:00:00Z').getTime() / 1000) : null,
            max: propertyFilters.auctionDateMax ? Math.floor(new Date(propertyFilters.auctionDateMax + 'T23:59:59Z').getTime() / 1000) : null
        });
    }

    return {
        field: firestoreField,
        order: firestoreOrder,
        limit: PAGE_SIZE,
        page,
        whereField: applyLeadType ? 'data.leadType' : null,
        whereValue: applyLeadType,
        whereField2: applySourceFilter ? 'data.customFields.leadsource'
                   : applyAutomationFilter ? 'data.automationId'
                   : null,
        whereValue2: applySourceFilter ? selectedSources
                   : applyAutomationFilter ? automationIdFilter
                   : null,
        whereOperator2: applySourceFilter ? 'in' : '==',
        assigneeFilter: propertyFilters.assignees?.length > 0 ? propertyFilters.assignees : undefined,
        searchField: debouncedSearch ? 'data.address.streetAddressLower' : null,
        searchValue: debouncedSearch ? debouncedSearch.toLowerCase() : null,
        boundsNorth: bounds?.north ?? null,
        boundsSouth: bounds?.south ?? null,
        boundsEast: bounds?.east ?? null,
        boundsWest: bounds?.west ?? null,
        tierFilter: null,
        underwritingFilter: applyLeadType === 'mls_deal' ? 'complete' : null,
        showArchived: archivedFilter || undefined,
        rangeFilters: rangeFilters.length > 0 ? rangeFilters : undefined,
        stringFilters: stringFilters.length > 0 ? stringFilters : undefined,
        // Server-side projection — see LEADS_LIST_FIELDS above.
        fields: LEADS_LIST_FIELDS
    };
}

// Stable hash that ignores `page` so we can identify "same query, different page"
// for prefetch/keepPreviousData behavior.
function paramsKey(params) {
    return JSON.stringify(params);
}

/**
 * Bundles all the page-metadata fetches (filter options, custom fields,
 * column prefs) into one round-trip. Doesn't include the leads array —
 * useLeadsPage runs in parallel for that, so the two requests don't block
 * each other and we don't pay for duplicate leads work server-side.
 *
 * Cached for 5 minutes per (company, user) pair: filter option sets change
 * very slowly relative to filter-toggle frequency, so this almost never
 * refetches in a session.
 */
export function useLeadsBootstrap({ companyId, userId, enabled = true }) {
    return useQuery({
        queryKey: ['leadsBootstrap', companyId, userId || null],
        enabled: enabled && !!companyId,
        staleTime: 5 * 60_000,
        gcTime: 10 * 60_000,
        queryFn: async () => {
            const url = process.env.REACT_APP_LEADS_BOOTSTRAP;
            const res = await cloudFunctionV2(url, {
                companyId,
                userId
                // intentionally omit leadsParams — see comment above
            });
            if (res.status !== 200) {
                throw new Error(res.error || res.text || 'leadsBootstrap failed');
            }
            return res;
        }
    });
}

/**
 * Page/filter follow-up fetcher. Keeps prior results visible while loading
 * (placeholderData) so sort/filter/paginate don't flash a skeleton.
 */
export function useLeadsPage({ companyId, leadsParams, enabled = true }) {
    const queryClient = useQueryClient();
    const lastDataRef = useRef(undefined);

    const query = useQuery({
        queryKey: ['leads', companyId, paramsKey(leadsParams)],
        enabled: enabled && !!companyId,
        staleTime: 30_000,
        placeholderData: (prev) => prev ?? lastDataRef.current,
        // No polling here: pending leads (ARV/repair still computing in
        // propertyIngestionV2) are watched event-driven via Firestore
        // onSnapshot in Leads.js, which invalidates this query once when a
        // lead finishes. getOrderedDocs is heavy (cold-starts ~20s, full-scans
        // the collection), so we only call it on an actual completion event,
        // not on a timer.
        queryFn: async () => {
            const url = process.env.REACT_APP_GET_ORDERED_DOCS;
            const res = await cloudFunctionV2(url, {
                ...leadsParams,
                collection: 'companies',
                subcollection: 'leads',
                document: companyId
            });
            if (res.status !== 200) {
                throw new Error(res.error || res.text || 'leads fetch failed');
            }
            return res;
        }
    });

    // Remember the last successful payload across query-key changes so the
    // first-render of a new key still has something to show.
    if (query.data) lastDataRef.current = query.data;

    // Prefetch the next page on idle — cheap insurance for snappy paginate.
    useEffect(() => {
        if (!query.data || !companyId) return;
        const totalPages = query.data.totalPages || 0;
        const nextPage = (leadsParams.page || 1) + 1;
        if (nextPage > totalPages) return;
        const nextParams = { ...leadsParams, page: nextPage };
        const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
        const cancel = window.cancelIdleCallback || clearTimeout;
        const handle = idle(() => {
            queryClient.prefetchQuery({
                queryKey: ['leads', companyId, paramsKey(nextParams)],
                staleTime: 30_000,
                queryFn: async () => {
                    const url = process.env.REACT_APP_GET_ORDERED_DOCS;
                    return await cloudFunctionV2(url, {
                        ...nextParams,
                        collection: 'companies',
                        subcollection: 'leads',
                        document: companyId
                    });
                }
            });
        });
        return () => cancel(handle);
    }, [query.data, companyId, leadsParams, queryClient]);

    return query;
}

export const LEADS_PAGE_SIZE = PAGE_SIZE;

// Hard ceiling on leads returned for a single map viewport. Mirrors the
// server's DEFAULT_LIMIT in `getLeadsInBounds.js` — if you change one,
// change the other. Airbnb-style: when viewport has >MAP_VIEWPORT_LIMIT
// matches, the UI shows a "zoom in for more" hint.
export const MAP_VIEWPORT_LIMIT = 200;

/**
 * Map-view fetcher. Uses the geohash-indexed `getLeadsInBounds` endpoint,
 * not the in-memory full-scan path that `useLeadsPage` rides through
 * `getOrderedDocs`. No pagination — viewport IS the filter.
 *
 * `leadsParams` is the same shape `useLeadsPage` consumes (output of
 * `buildLeadsParams`). The server reads whichever filter keys it recognizes
 * (whereField2/Value2, assigneeFilter, searchField/Value, rangeFilters,
 * stringFilters, underwritingFilter, dealStatusFilter, showArchived, fields)
 * and ignores pagination/sort. Sharing the shape keeps map and table view
 * in lock-step on every filter dimension — no silent drops when toggling.
 *
 * Mirrors `useLeadsPage`'s placeholderData pattern so panning doesn't
 * flash a blank rail.
 */
export function useLeadsInBounds({ companyId, bounds, leadsParams, enabled = true }) {
    const lastDataRef = useRef(undefined);

    const params = bounds && leadsParams ? {
        companyId,
        boundsNorth: bounds.north,
        boundsSouth: bounds.south,
        boundsEast: bounds.east,
        boundsWest: bounds.west,
        limit: MAP_VIEWPORT_LIMIT,
        // Forward every filter dimension the table view uses. Server picks
        // out what it understands; page/sort/order/limit/bounds* in leadsParams
        // are intentionally ignored on the server side for this endpoint.
        whereField: leadsParams.whereField,
        whereValue: leadsParams.whereValue,
        whereField2: leadsParams.whereField2,
        whereValue2: leadsParams.whereValue2,
        whereOperator2: leadsParams.whereOperator2,
        assigneeFilter: leadsParams.assigneeFilter,
        searchField: leadsParams.searchField,
        searchValue: leadsParams.searchValue,
        underwritingFilter: leadsParams.underwritingFilter,
        dealStatusFilter: leadsParams.dealStatusFilter,
        showArchived: leadsParams.showArchived,
        rangeFilters: leadsParams.rangeFilters,
        stringFilters: leadsParams.stringFilters,
        fields: leadsParams.fields || LEADS_LIST_FIELDS
    } : null;

    const query = useQuery({
        queryKey: ['leadsInBounds', companyId, params ? JSON.stringify(params) : null],
        enabled: enabled && !!companyId && !!bounds && !!leadsParams,
        staleTime: 30_000,
        placeholderData: (prev) => prev ?? lastDataRef.current,
        queryFn: async () => {
            const url = process.env.REACT_APP_GET_LEADS_IN_BOUNDS;
            if (!url) throw new Error('REACT_APP_GET_LEADS_IN_BOUNDS not configured');
            const res = await cloudFunctionV2(url, params);
            if (res.status !== 200) {
                throw new Error(res.error || res.text || 'getLeadsInBounds failed');
            }
            return res;
        }
    });

    if (query.data) lastDataRef.current = query.data;

    return query;
}
